import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { invoicesAPI, customersAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Search, DollarSign, Send, Clock, CheckCircle2, Eye, Pencil, Copy, Link } from 'lucide-react';

// Standard service types with lawn checked by default
const SERVICE_TYPES = [
  { id: 'lawn', label: 'Lawn Mowing', defaultChecked: true },
  { id: 'edge', label: 'Edging', defaultChecked: false },
  { id: 'weed_control', label: 'Weed Control', defaultChecked: false },
  { id: 'overgrowth', label: 'Overgrowth', defaultChecked: false },
  { id: 'leaves', label: 'Leaf Removal', defaultChecked: false },
  { id: 'bagging', label: 'Bagging', defaultChecked: false },
  { id: 'no_access', label: 'No Access Fee', defaultChecked: false },
];

const emptyLineItems = () => SERVICE_TYPES.map(s => ({
  service_type: s.id,
  label: s.label,
  checked: s.defaultChecked,
  amount: ''
}));

export default function Invoices() {
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  const [formData, setFormData] = useState({
    customer_id: '',
    line_items: emptyLineItems(),
    other_description: '',
    other_amount: '',
    additional_notes: '',
    due_date: '',
    service_date: ''
  });

  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_method: 'cash',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const [invoicesRes, customersRes] = await Promise.all([
        invoicesAPI.getAll(params),
        customersAPI.getAll()
      ]);
      
      setInvoices(invoicesRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerSelect = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    setSelectedCustomer(customer);
    
    // Pre-fill line items with customer's rate for lawn
    const newLineItems = emptyLineItems().map(item => {
      if (item.service_type === 'lawn' && customer) {
        return { ...item, amount: customer.cost_per_service?.toString() || '' };
      }
      return item;
    });
    
    setFormData({ ...formData, customer_id: customerId, line_items: newLineItems });
  };

  const toggleLineItem = (index, checked) => {
    const newItems = [...formData.line_items];
    newItems[index] = { ...newItems[index], checked };
    setFormData({ ...formData, line_items: newItems });
  };

  const updateLineItemAmount = (index, amount) => {
    const newItems = [...formData.line_items];
    newItems[index] = { ...newItems[index], amount };
    setFormData({ ...formData, line_items: newItems });
  };

  const buildLineItems = () => {
    const items = [];
    
    formData.line_items.forEach(item => {
      if (item.checked && item.amount) {
        items.push({
          service_type: item.service_type,
          description: item.label,
          amount: parseFloat(item.amount)
        });
      }
    });
    
    if (formData.other_description && formData.other_amount) {
      items.push({
        service_type: 'other',
        description: formData.other_description,
        amount: parseFloat(formData.other_amount)
      });
    }
    
    return items;
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    
    const lineItems = buildLineItems();
    
    if (!formData.customer_id || lineItems.length === 0 || !formData.due_date) {
      toast.error('Please select a customer, add at least one service, and set a due date');
      return;
    }

    try {
      await invoicesAPI.create({
        customer_id: formData.customer_id,
        line_items: lineItems,
        additional_notes: formData.additional_notes || null,
        due_date: formData.due_date,
        service_date: formData.service_date || null
      });
      
      toast.success('Invoice created');
      setCreateDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Create invoice error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      line_items: emptyLineItems(),
      other_description: '',
      other_amount: '',
      additional_notes: '',
      due_date: '',
      service_date: ''
    });
    setSelectedCustomer(null);
  };

  const handleRecordPayment = async () => {
    if (!paymentData.amount) {
      toast.error('Please enter payment amount');
      return;
    }

    try {
      await invoicesAPI.recordPayment(selectedInvoice.id, {
        invoice_id: selectedInvoice.id,
        amount: parseFloat(paymentData.amount),
        payment_method: paymentData.payment_method,
        notes: paymentData.notes || null
      });
      
      toast.success('Payment recorded');
      setPaymentDialogOpen(false);
      setSelectedInvoice(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  const handleSendReminder = async (invoiceId) => {
    try {
      await invoicesAPI.sendReminder(invoiceId);
      toast.success('Reminder sent');
    } catch (error) {
      toast.error('Failed to send reminder');
    }
  };

  const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  const copyInvoiceLink = (token) => {
    const link = `${baseUrl}/invoice/${token}`;
    navigator.clipboard.writeText(link);
    toast.success('Invoice payment link copied! Send this to your customer.');
  };

  const openPaymentDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentData({
      amount: invoice.amount.toString(),
      payment_method: 'cash',
      notes: ''
    });
    setPaymentDialogOpen(true);
  };

  const openViewDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setViewDialogOpen(true);
  };

  const openEditDialog = (invoice) => {
    setSelectedInvoice(invoice);
    
    // Reconstruct line_items from invoice data
    const existingItems = invoice.line_items || [];
    const newLineItems = emptyLineItems().map(item => {
      const existing = existingItems.find(e => e.service_type === item.service_type);
      if (existing) {
        return { ...item, checked: true, amount: existing.amount?.toString() || '' };
      }
      return { ...item, checked: false, amount: '' };
    });
    
    // Check for 'other' items
    const otherItem = existingItems.find(e => e.service_type === 'other');
    
    setFormData({
      customer_id: invoice.customer_id,
      line_items: newLineItems,
      other_description: otherItem?.description || '',
      other_amount: otherItem?.amount?.toString() || '',
      additional_notes: invoice.additional_notes || '',
      due_date: invoice.due_date || '',
      service_date: invoice.service_date || ''
    });
    
    const customer = customers.find(c => c.id === invoice.customer_id);
    setSelectedCustomer(customer);
    setEditDialogOpen(true);
  };

  const handleUpdateInvoice = async (e) => {
    e.preventDefault();
    
    const lineItems = buildLineItems();
    
    if (lineItems.length === 0 || !formData.due_date) {
      toast.error('Please add at least one service and set a due date');
      return;
    }

    try {
      await invoicesAPI.update(selectedInvoice.id, {
        line_items: lineItems,
        additional_notes: formData.additional_notes || null,
        due_date: formData.due_date,
        service_date: formData.service_date || null
      });
      
      toast.success('Invoice updated');
      setEditDialogOpen(false);
      setSelectedInvoice(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Update invoice error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update invoice');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'status-pending',
      'paid': 'status-paid',
      'overdue': 'status-overdue',
      'cancelled': 'bg-gray-200 text-gray-600'
    };
    return colors[status] || 'bg-gray-200 text-gray-600';
  };

  const filteredInvoices = invoices.filter(inv =>
    inv.invoice_number.toLowerCase().includes(search.toLowerCase()) ||
    inv.customer_name?.toLowerCase().includes(search.toLowerCase())
  );

  const calculateTotal = () => {
    let total = 0;
    formData.line_items.forEach(item => {
      if (item.checked && item.amount) {
        total += parseFloat(item.amount) || 0;
      }
    });
    if (formData.other_amount) {
      total += parseFloat(formData.other_amount) || 0;
    }
    return total;
  };

  // Render the line items form (reused for create and edit)
  const renderLineItemsForm = () => (
    <div className="space-y-3">
      <Label className="text-base font-semibold">Services</Label>
      <div className="border border-gray-600 rounded-lg p-4 space-y-3 bg-gray-800">
        {formData.line_items.map((item, idx) => (
          <div key={item.service_type} className="flex items-center gap-3">
            <Checkbox
              id={item.service_type}
              checked={item.checked}
              onCheckedChange={(checked) => toggleLineItem(idx, checked)}
            />
            <label htmlFor={item.service_type} className="flex-1 text-sm cursor-pointer text-gray-200">
              {item.label}
            </label>
            <div className="w-24">
              <Input
                type="number"
                placeholder="$0"
                value={item.amount}
                onChange={(e) => updateLineItemAmount(idx, e.target.value)}
                disabled={!item.checked}
                className="h-8 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>
          </div>
        ))}
        
        {/* Other/Custom line item */}
        <div className="border-t border-gray-600 pt-3 mt-3">
          <Label className="text-sm text-gray-400 mb-2 block">Other (custom charge)</Label>
          <div className="flex gap-2">
            <Input
              placeholder="Description"
              value={formData.other_description}
              onChange={(e) => setFormData({...formData, other_description: e.target.value})}
              className="flex-1 h-8 text-sm"
            />
            <Input
              type="number"
              placeholder="$0"
              value={formData.other_amount}
              onChange={(e) => setFormData({...formData, other_amount: e.target.value})}
              className="w-24 h-8 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          </div>
        </div>
        
        {/* Total */}
        <div className="border-t border-gray-600 pt-3 mt-3 flex justify-between items-center">
          <span className="font-semibold text-gray-200">Total:</span>
          <span className="text-xl font-bold text-lime-500">${calculateTotal().toFixed(2)}</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Invoices</h1>
          <p className="text-gray-500 mt-1">Manage billing and payments</p>
        </div>
        
        <Dialog open={createDialogOpen} onOpenChange={(open) => {
          setCreateDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-lime-500 hover:bg-lime-600 text-black" data-testid="create-invoice-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Invoice
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Invoice</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateInvoice} className="space-y-4 mt-4">
              <div>
                <Label>Customer *</Label>
                <Select 
                  value={formData.customer_id} 
                  onValueChange={handleCustomerSelect}
                >
                  <SelectTrigger data-testid="invoice-customer-select">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.filter(c => c.status === 'active').map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.contact_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedCustomer && (
                  <p className="text-sm text-gray-500 mt-1">
                    Default rate: ${selectedCustomer.cost_per_service?.toFixed(2) || '0.00'} ({selectedCustomer.billing_plan})
                  </p>
                )}
              </div>

              {renderLineItemsForm()}

              <div>
                <Label>Additional Notes</Label>
                <Textarea
                  value={formData.additional_notes}
                  onChange={(e) => setFormData({...formData, additional_notes: e.target.value})}
                  placeholder="Any additional notes for this invoice..."
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Due Date *</Label>
                  <Input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                    data-testid="invoice-due-date-input"
                  />
                </div>
                <div>
                  <Label>Service Date</Label>
                  <Input
                    type="date"
                    value={formData.service_date}
                    onChange={(e) => setFormData({...formData, service_date: e.target.value})}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-lime-500 hover:bg-lime-600 text-black" data-testid="invoice-submit-btn">
                  Create Invoice
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search invoices..."
                className="pl-10"
                data-testid="invoice-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Invoice List */}
      {loading ? (
        <div className="space-y-4">
          {[1,2,3].map(i => <div key={i} className="h-24 bg-gray-200 rounded-lg animate-pulse" />)}
        </div>
      ) : filteredInvoices.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-8 text-center">
            <p className="text-gray-500">No invoices found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredInvoices.map((invoice) => (
            <Card key={invoice.id} className="border-0 shadow-sm" data-testid={`invoice-card-${invoice.id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg text-gray-900">{invoice.invoice_number}</h3>
                      <span className={`status-badge ${getStatusColor(invoice.status)}`}>
                        {invoice.status}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-2">{invoice.customer_name}</p>
                    <p className="text-sm text-gray-500">{invoice.description}</p>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500 mt-2">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        Due: {new Date(invoice.due_date).toLocaleDateString()}
                      </span>
                      {invoice.paid_at && (
                        <span className="flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                          Paid: {new Date(invoice.paid_at).toLocaleDateString()}
                        </span>
                      )}
                      {invoice.days_to_payment !== undefined && invoice.days_to_payment !== null && (
                        <span className="text-gray-400">
                          ({invoice.days_to_payment} days to pay)
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-bold text-2xl text-lime-600">${invoice.amount.toFixed(2)}</p>
                      {invoice.payment_method && (
                        <p className="text-xs text-gray-500 capitalize">via {invoice.payment_method}</p>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openViewDialog(invoice)}
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditDialog(invoice)}
                        title="Edit Invoice"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      {invoice.token && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyInvoiceLink(invoice.token)}
                          title="Copy Payment Link"
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <Link className="w-4 h-4" />
                        </Button>
                      )}
                      {(invoice.status === 'pending' || invoice.status === 'overdue') && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSendReminder(invoice.id)}
                            title={invoice.status === 'overdue' ? 'Resend Invoice' : 'Send Reminder'}
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            className="bg-lime-500 hover:bg-lime-600 text-black"
                            onClick={() => openPaymentDialog(invoice)}
                          >
                            <DollarSign className="w-4 h-4 mr-1" />
                            Record Payment
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* View Invoice Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Invoice Details</DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4 mt-4">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold">{selectedInvoice.invoice_number}</h2>
                  <p className="text-gray-600">{selectedInvoice.customer_name}</p>
                </div>
                <span className={`status-badge ${getStatusColor(selectedInvoice.status)}`}>
                  {selectedInvoice.status}
                </span>
              </div>

              {/* Payment Link Section */}
              {selectedInvoice.token && selectedInvoice.status !== 'paid' && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <Label className="text-blue-700 font-semibold text-sm">Payment Link (share with customer):</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      value={`${baseUrl}/invoice/${selectedInvoice.token}`}
                      readOnly
                      className="bg-white text-sm"
                    />
                    <Button
                      size="sm"
                      className="bg-blue-500 hover:bg-blue-600 text-white"
                      onClick={() => copyInvoiceLink(selectedInvoice.token)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
              
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-2 text-sm font-medium text-gray-600">Service</th>
                      <th className="text-right px-4 py-2 text-sm font-medium text-gray-600">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedInvoice.line_items || []).map((item, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-4 py-3 text-sm">{item.description}</td>
                        <td className="px-4 py-3 text-sm text-right">${item.amount?.toFixed(2)}</td>
                      </tr>
                    ))}
                    <tr className="border-t bg-gray-50">
                      <td className="px-4 py-3 font-semibold">Total</td>
                      <td className="px-4 py-3 text-right font-bold text-lime-600">${selectedInvoice.amount?.toFixed(2)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Service Date</p>
                  <p className="font-medium">{selectedInvoice.service_date ? new Date(selectedInvoice.service_date).toLocaleDateString() : 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Due Date</p>
                  <p className="font-medium">{new Date(selectedInvoice.due_date).toLocaleDateString()}</p>
                </div>
                {selectedInvoice.paid_at && (
                  <>
                    <div>
                      <p className="text-gray-500">Paid Date</p>
                      <p className="font-medium">{new Date(selectedInvoice.paid_at).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Payment Method</p>
                      <p className="font-medium capitalize">{selectedInvoice.payment_method}</p>
                    </div>
                  </>
                )}
              </div>
              
              {selectedInvoice.additional_notes && (
                <div className="border-t pt-4">
                  <p className="text-gray-500 text-sm mb-1">Notes</p>
                  <p className="text-sm">{selectedInvoice.additional_notes}</p>
                </div>
              )}
              
              <div className="flex justify-end pt-4">
                <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Invoice Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => {
        setEditDialogOpen(open);
        if (!open) {
          setSelectedInvoice(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Invoice {selectedInvoice?.invoice_number}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateInvoice} className="space-y-4 mt-4">
            {selectedCustomer && (
              <div className="p-3 bg-gray-800 border border-gray-600 rounded-lg">
                <p className="font-medium text-gray-100">{selectedCustomer.contact_name}</p>
                <p className="text-sm text-gray-400">
                  Default rate: ${selectedCustomer.cost_per_service?.toFixed(2) || '0.00'}
                </p>
              </div>
            )}

            {renderLineItemsForm()}

            <div>
              <Label>Additional Notes</Label>
              <Textarea
                value={formData.additional_notes}
                onChange={(e) => setFormData({...formData, additional_notes: e.target.value})}
                placeholder="Any additional notes for this invoice..."
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Due Date *</Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                />
              </div>
              <div>
                <Label>Service Date</Label>
                <Input
                  type="date"
                  value={formData.service_date}
                  onChange={(e) => setFormData({...formData, service_date: e.target.value})}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-lime-500 hover:bg-lime-600 text-black">
                Update Invoice
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-gray-800 border border-gray-600 rounded-lg">
                <p className="font-medium text-gray-100">{selectedInvoice.invoice_number}</p>
                <p className="text-sm text-gray-400">{selectedInvoice.customer_name}</p>
                <p className="text-lg font-bold text-lime-500 mt-2">${selectedInvoice.amount.toFixed(2)}</p>
              </div>

              <div>
                <Label>Payment Amount *</Label>
                <Input
                  type="number"
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData({...paymentData, amount: e.target.value})}
                  data-testid="payment-amount-input"
                  className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>

              <div>
                <Label>Payment Method</Label>
                <Select 
                  value={paymentData.payment_method} 
                  onValueChange={(v) => setPaymentData({...paymentData, payment_method: v})}
                >
                  <SelectTrigger data-testid="payment-method-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="cash_app">Cash App</SelectItem>
                    <SelectItem value="venmo">Venmo</SelectItem>
                    <SelectItem value="stripe">Credit Card (Stripe)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={paymentData.notes}
                  onChange={(e) => setPaymentData({...paymentData, notes: e.target.value})}
                  placeholder="Any notes about this payment..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  className="bg-lime-500 hover:bg-lime-600 text-black"
                  onClick={handleRecordPayment}
                  data-testid="record-payment-btn"
                >
                  Record Payment
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
